import { PageDataHandler } from "@/lib/utility/data/page-data-handler";
import { ActivityLog, IActivityLog } from "@/models/activity-log.entity";
import { IPageFilterElement, PageFilterElement } from "@/models/page-filter-element.entity";
import { MrfiktivActivityLogControllerFindAllParamsGen } from "@/services/mrfiktiv/v1/data-contracts";
import { BackendResourceEnum } from "@/store/enum/authResourceEnum";
import { PaginatedBaseStore } from "@/store/paginated-base.store";
import store from "@/store/VuexPlugin";
import { Action, Mutation, getModule, Module } from "vuex-module-decorators";
import { ActivityInboxDataAccessLayer } from "./access-layers/activity-inbox.access-layer";
import { PaginationFilterListElement, PaginationFilterOperationEnum } from "./base-pagination.store";
import { ActivityInboxPageDataProvider } from "./page-data-provider/activity-inbox.page-data-provider";

/**
 * The filters that scope the activity log to the activities the given user received.
 *
 * Mirrors what the backend stores: the user is one of the `target` references of the activity.
 */
export function activityInboxFiltersForUser(userId: string): IPageFilterElement[] {
  return [
    new PageFilterElement({
      key: "target.refId",
      operation: PaginationFilterOperationEnum.EQUAL,
      value: userId
    }),
    new PageFilterElement({
      key: "target.refType",
      operation: PaginationFilterOperationEnum.EQUAL,
      value: BackendResourceEnum.USER
    })
  ];
}

/**
 * The inbox of the logged-in user: every activity that references the user as a target, e.g. a comment
 * mentioning them or a resource they were assigned to.
 *
 * The backend has no read state for activities, so an entry is only "new" for as long as the user has
 * not opened the inbox in this session: activities arriving over the websocket are remembered in
 * `unseenIds` and the whole list is dropped the moment the inbox is opened.
 */
@Module({
  dynamic: true,
  namespaced: true,
  name: "activity-inbox",
  store
})
export class ActivityInboxStore extends PaginatedBaseStore<
  IActivityLog,
  MrfiktivActivityLogControllerFindAllParamsGen
> {
  protected _data = ActivityInboxDataAccessLayer;
  protected _pageProvider = ActivityInboxPageDataProvider;
  protected _pager = new PageDataHandler(this._data, this._pageProvider);

  filterOptions: PaginationFilterListElement[] = ActivityLog.filterables;

  /**
   * The ids of the activities that arrived over the websocket since the inbox was last opened
   */
  private _unseenIds: string[] = [];

  get unseenCount(): number {
    return this._unseenIds.length;
  }

  get hasUnseen(): boolean {
    return this._unseenIds.length > 0;
  }

  @Mutation
  private _mutateUnseenIds(unseenIds: string[]) {
    this._unseenIds = unseenIds;
  }

  /**
   * Adds an activity that arrived over the websocket to the inbox.
   *
   * @param payload.activity the activity that was created
   * @param payload.badge whether the activity marks the inbox as new. `false` for an activity the user
   * triggered themselves, e.g. assigning themselves to a ticket: they do not need to be notified of it.
   */
  @Action
  receive(payload: { activity: IActivityLog; badge: boolean }) {
    this._data.set(payload.activity);

    if (payload.badge) {
      this.context.dispatch("markUnseen", payload.activity.id);
    }
  }

  @Action
  markUnseen(activityId: string) {
    if (!activityId || this._unseenIds.includes(activityId)) {
      return;
    }

    this.context.commit("_mutateUnseenIds", [...this._unseenIds, activityId]);
  }

  /**
   * Drops the badge. Called when the user opens the inbox: without a read state on the backend,
   * opening it is the only signal that the activities were looked at.
   */
  @Action
  markAllSeen() {
    if (!this._unseenIds.length) {
      return;
    }

    this.context.commit("_mutateUnseenIds", []);
  }

  /**
   * @inheritdoc
   */
  @Action
  reset() {
    this._data.clear();
    this.context.commit("_mutateUnseenIds", []);
  }
}

export const ActivityInboxModule = getModule(ActivityInboxStore);
